import { useCallback, useEffect, useRef, useState } from 'react';
import { chatService } from '../../../services/chatService';
import {
  createTransferRequest,
  getMyTransferRequests,
  type CwyGrantBlockReason,
  type CwyTransferRequest,
} from '../services/walletService';
import { isActiveGrantRequest } from '../utils/transferProgress';

const POLL_MS = 4000;
const TERMINAL_HOLD_MS = 8000;

function mergeTransferRow(items: CwyTransferRequest[], incoming: CwyTransferRequest): CwyTransferRequest[] {
  const idx = items.findIndex((r) => r.transfer_request_id === incoming.transfer_request_id);
  if (idx >= 0) {
    const next = [...items];
    next[idx] = { ...items[idx], ...incoming };
    return next;
  }
  return [incoming, ...items];
}

export function useCwyTransferRequests(enabled = true) {
  const [requests, setRequests] = useState<CwyTransferRequest[]>([]);
  const [grantRedeemed, setGrantRedeemed] = useState(false);
  const [walletGrantRedeemed, setWalletGrantRedeemed] = useState(false);
  const [blockReason, setBlockReason] = useState<CwyGrantBlockReason>(null);
  const [grantLimitsEnforced, setGrantLimitsEnforced] = useState(true);
  const [softGrantLimits, setSoftGrantLimits] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requesting, setRequesting] = useState(false);
  const [heldProgressRequest, setHeldProgressRequest] = useState<CwyTransferRequest | null>(null);

  const heldTimerRef = useRef<number | undefined>(undefined);
  const prevActiveIdRef = useRef<string | null>(null);

  const applyListResponse = useCallback(
    (data: Awaited<ReturnType<typeof getMyTransferRequests>>) => {
      setRequests(data.items);
      setGrantRedeemed(data.grant_redeemed);
      setWalletGrantRedeemed(data.wallet_grant_redeemed);
      setBlockReason(data.block_reason);
      setGrantLimitsEnforced(data.grant_limits_enforced);
      setSoftGrantLimits(data.soft_grant_limits);
    },
    []
  );

  const load = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!enabled) return;
      if (!options?.silent) {
        setLoading(true);
      }
      setError(null);
      try {
        const data = await getMyTransferRequests();
        applyListResponse(data);
      } catch (e) {
        if (!options?.silent) {
          setError(e instanceof Error ? e.message : 'Failed to load transfer requests');
        }
      } finally {
        if (!options?.silent) {
          setLoading(false);
        }
      }
    },
    [enabled, applyListResponse]
  );

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    void load();
  }, [enabled, load]);

  const activeGrantRequest = requests.find(isActiveGrantRequest);

  const progressRequest = activeGrantRequest ?? heldProgressRequest;

  useEffect(() => {
    if (activeGrantRequest) {
      prevActiveIdRef.current = activeGrantRequest.transfer_request_id;
      setHeldProgressRequest(null);
      if (heldTimerRef.current) {
        window.clearTimeout(heldTimerRef.current);
        heldTimerRef.current = undefined;
      }
      return;
    }

    const prevId = prevActiveIdRef.current;
    if (!prevId) return;

    const terminal = requests.find(
      (r) =>
        r.transfer_request_id === prevId &&
        (r.status === 'completed' || r.status === 'rejected' || r.status === 'failed')
    );

    if (terminal) {
      setHeldProgressRequest(terminal);
      prevActiveIdRef.current = null;
      if (heldTimerRef.current) {
        window.clearTimeout(heldTimerRef.current);
      }
      heldTimerRef.current = window.setTimeout(() => {
        setHeldProgressRequest(null);
        heldTimerRef.current = undefined;
      }, TERMINAL_HOLD_MS);
    }
  }, [requests, activeGrantRequest]);

  useEffect(() => {
    if (!enabled || !activeGrantRequest) return;
    const id = window.setInterval(() => void load({ silent: true }), POLL_MS);
    return () => window.clearInterval(id);
  }, [enabled, activeGrantRequest?.transfer_request_id, activeGrantRequest?.status, load]);

  useEffect(() => {
    if (!enabled) return;

    const attachSocket = () => {
      const socket = chatService.getSocket();
      if (!socket) return undefined;

      const onTransferEvent = (payload: { transfer?: CwyTransferRequest }) => {
        const transfer = payload?.transfer;
        if (!transfer?.transfer_request_id) return;
        setRequests((prev) => mergeTransferRow(prev, transfer));
      };

      socket.on('cwy-transfer:created', onTransferEvent);
      socket.on('cwy-transfer:updated', onTransferEvent);
      return () => {
        socket.off('cwy-transfer:created', onTransferEvent);
        socket.off('cwy-transfer:updated', onTransferEvent);
      };
    };

    let cleanup = attachSocket();
    if (cleanup) return cleanup;

    const retryId = window.setInterval(() => {
      cleanup?.();
      cleanup = attachSocket();
      if (cleanup) {
        window.clearInterval(retryId);
      }
    }, 2000);

    return () => {
      window.clearInterval(retryId);
      cleanup?.();
    };
  }, [enabled]);

  useEffect(
    () => () => {
      if (heldTimerRef.current) {
        window.clearTimeout(heldTimerRef.current);
      }
    },
    []
  );

  const requestCwy = useCallback(
    async (amount: number) => {
      setRequesting(true);
      setError(null);
      try {
        const transfer = await createTransferRequest(amount);
        setRequests((prev) => mergeTransferRow(prev, transfer));
        await load({ silent: true });
      } catch (e: unknown) {
        const err = e as { response?: { data?: { message?: string } }; message?: string };
        setError(err.response?.data?.message || err.message || 'Failed to request CWY');
        throw e;
      } finally {
        setRequesting(false);
      }
    },
    [load]
  );

  const activeRequest = activeGrantRequest;
  const latestRequest = requests[0];
  const completedGrant = requests.find(
    (r) =>
      (r.request_type ?? 'phase1_test_grant') === 'phase1_test_grant' && r.status === 'completed'
  );
  const redemptionBlocked = grantLimitsEnforced && (grantRedeemed || walletGrantRedeemed);

  return {
    requests,
    loading,
    error,
    requesting,
    activeRequest,
    activeGrantRequest,
    progressRequest,
    latestRequest,
    completedGrant,
    grantRedeemed,
    walletGrantRedeemed,
    blockReason,
    grantLimitsEnforced,
    softGrantLimits,
    canRequestNewGrant: !redemptionBlocked && !requesting && !activeGrantRequest,
    refresh: load,
    requestCwy,
  };
}
